import { useEffect, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileImage } from "@/components/FileImage";
import { ShieldCheck, AlertTriangle, Camera } from "lucide-react";

const PP_TEXT = "I certify this search is for service of process in a legal proceeding (permissible purpose).";
const CLOSE_REASONS = [
  ["case_resolved", "Case settled, dismissed or withdrawn"],
  ["served_other_way", "Defendant served another way or waived service"],
  ["hearing_changed", "Hearing or deadline canceled, stayed or continued"],
  ["order_in_error", "Order entered in error (flag for admin review)"],
  ["other", "Other"],
];
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

function timeWindow(ts) {
  if (!ts) return "";
  try {
    const h = new Date(ts).getHours();
    if (h < 12) return "Morning";
    if (h < 17) return "Afternoon";
    return "Evening";
  } catch { return ""; }
}
const fmtDate = (ts) => { try { return new Date(ts).toLocaleDateString(); } catch { return ""; } };
const fmtTime = (ts) => { try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function AttemptSummary({ a }) {
  const at = a.device_timestamp || a.timestamp;
  const served = a.outcome === "served";
  const gpsVerified = a.verified && (a.gps?.validation?.status === "pass");
  return (
    <div data-testid={`decision-attempt-${a.attempt_number}`} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-slate-800">Attempt #{a.attempt_number}</span>
        <span className="text-xs text-slate-500">{fmtDate(at)} · {timeWindow(at)} · {fmtTime(at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 font-semibold ${served ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{cap(a.outcome)}</span>
        {a.not_served_reason && <span className="text-slate-600">Reason: <b>{cap(a.not_served_reason)}</b></span>}
        {a.gps_recorded !== undefined
          ? <span data-testid={`decision-recorded-${a.attempt_number}`} className="text-slate-600">GPS recorded: <b>{a.gps_recorded ? "Yes" : "No"}</b> · Photo recorded: <b>{a.photo_recorded ? "Yes" : "No"}</b></span>
          : gpsVerified
          ? <span data-testid={`decision-gps-verified-${a.attempt_number}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> GPS verified</span>
          : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> Not GPS-verified</span>}
      </div>
      {a.no_access_barrier && <p className="mt-1 text-xs text-slate-600">Barrier: <b>{cap(a.no_access_barrier)}</b>{a.measures_taken?.length ? ` · Measures: ${a.measures_taken.map(cap).join(", ")}` : ""}</p>}
      {a.spoke_with && <p className="mt-1 text-xs text-slate-600">Spoke with: <b>{cap(a.spoke_with)}</b>{a.spoke_with_statement ? ` — ${cap(a.spoke_with_statement)}` : ""}</p>}
      {(a.photos || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {a.photos.map((p, i) => {
            const fid = typeof p === "string" ? p : (p.file_id || p.id);
            return fid ? <FileImage key={i} fileId={fid} className="h-12 w-12 rounded border border-slate-200 object-cover" /> : null;
          })}
          {a.photos.length === 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Camera className="h-3 w-3" /> No photo</span>}
        </div>
      )}
    </div>
  );
}

export const ClientDecisionPanel = ({ order, role }) => {
  const cd = order?.client_decision || {};
  const isClient = role === "client";
  const hasSkip = !!order?.skip_trace;
  const [busy, setBusy] = useState("");
  const [skipForm, setSkipForm] = useState(null);
  const [addr, setAddr] = useState("");
  const [pp, setPp] = useState(false);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [newAddr, setNewAddr] = useState({ street: "", city: "", state: "", zip: "" });
  const [attempts, setAttempts] = useState([]);
  const isBarrier = cd.trigger === "access_barrier";
  const [showAccess, setShowAccess] = useState(false);
  const [access, setAccess] = useState({ gate_code: "", contact_name: "", contact_phone: "", available_datetime: "" });
  const [showClose, setShowClose] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeText, setCloseText] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!order?.id) return;
    api.get(`/orders/${order.id}/evidence`).then(({ data }) => setAttempts(data.attempts || [])).catch(() => {});
  }, [order?.id]);

  const startCheckout = async (choice, extra = {}) => {
    setBusy(choice);
    try {
      const { data } = await api.post(`/orders/${order.id}/client-decision`, { choice, ...extra });
      const { data: co } = await api.post(`/invoices/${data.invoice_id}/stripe/checkout`, { origin_url: window.location.origin });
      if (co?.checkout_url) { window.location.href = co.checkout_url; return; }
      toast.error("Could not start checkout.");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not start this option."));
    } finally { setBusy(""); }
  };

  const submitSkip = () => {
    if (!addr.trim()) { toast.error("Last known address is required."); return; }
    if (!pp) { toast.error("Please certify the permissible purpose."); return; }
    startCheckout(skipForm === "advanced" ? "skip_trace_advanced" : "skip_trace_basic", {
      permissible_purpose_accepted: true,
      skip_trace_info: { defendant_name: order?.defendant || "", last_known_address: addr.trim() },
    });
  };

  const submitAddress = () => {
    if (!newAddr.street.trim()) { toast.error("Enter the new service address."); return; }
    startCheckout("additional_address", { new_address: newAddr });
  };

  const submitAccessInfo = async () => {
    if (!Object.values(access).some((v) => (v || "").trim())) { toast.error("Enter at least one access detail."); return; }
    setBusy("send_access_info");
    try {
      await api.post(`/orders/${order.id}/client-decision`, { choice: "send_access_info", access_info: access });
      toast.success("Access info sent — the server will resume service.");
      window.location.reload();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not send access info.")); }
    finally { setBusy(""); }
  };

  const openClose = async () => {
    setShowClose(true);
    if (isBarrier) {
      try { const { data } = await api.get(`/orders/${order.id}/close-preview`); setPreview(data); } catch { /* noop */ }
    }
  };

  const submitBarrierClose = async () => {
    if (!closeReason) { toast.error("Select a reason to close."); return; }
    if (closeReason === "other" && !closeText.trim()) { toast.error("Describe the reason to close."); return; }
    setBusy("close");
    try {
      await api.post(`/orders/${order.id}/client-decision`, { choice: "close", close_reason: closeReason, close_reason_text: closeText.trim() });
      toast.success("Order will be closed with a Return of Non-Service.");
      window.location.reload();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not close the order.")); }
    finally { setBusy(""); }
  };

  const closeReturn = async () => {
    if (!window.confirm("Close this order with a Return of Non-Service? The assigned server will review and sign it.")) return;
    setBusy("close_return");
    try {
      await api.post(`/orders/${order.id}/client-decision`, { choice: "close_return" });
      toast.success("Order will be closed with a Return of Non-Service.");
      window.location.reload();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not close the order.")); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="client-decision-panel" className="rounded-xl border border-amber-300 bg-amber-50/70 p-5">
      <h3 className="font-display text-base font-bold text-slate-900">Service update — choose how to proceed</h3>
      <p className="mt-2 text-sm text-slate-700" data-testid="client-decision-message">
        {isBarrier
          ? "Our process server documented an access barrier at the service address. Review the attempt below, then choose how to proceed."
          : "We were unable to complete service after 4 attempts (morning, afternoon, evening, and weekend). Service success can't be guaranteed. Review each attempt below, then choose how to proceed:"}
      </p>
      {isBarrier && (
        <p className="mt-1 text-xs font-medium text-slate-500" data-testid="client-decision-disclaimer">
          You or your attorney decide how to proceed. ARPS documents the attempts and does not advise on alternative methods of service.
        </p>
      )}

      {attempts.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="decision-attempts-list">
          {attempts.map((a) => <AttemptSummary key={a.attempt_number} a={a} />)}
        </div>
      )}

      {cd.state === "awaiting" && isClient ? (
        <div className="mt-4 space-y-3">
          {isBarrier && (!showAccess ? (
            <Button data-testid="decision-send-access" disabled={!!busy} onClick={() => setShowAccess(true)}
              className="w-full justify-start bg-white text-slate-900 border border-slate-300 hover:bg-slate-50">
              Send access info (gate code, contact, available time)
            </Button>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="decision-access-form">
              <p className="text-sm font-semibold text-slate-800">Access info for the server</p>
              <Input data-testid="access-gate-code" value={access.gate_code} onChange={(e) => setAccess({ ...access, gate_code: e.target.value })} placeholder="Gate / door code" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="access-contact-name" value={access.contact_name} onChange={(e) => setAccess({ ...access, contact_name: e.target.value })} placeholder="Contact name" />
                <Input data-testid="access-contact-phone" value={access.contact_phone} onChange={(e) => setAccess({ ...access, contact_phone: e.target.value })} placeholder="Contact phone" />
              </div>
              <Input data-testid="access-available" value={access.available_datetime} onChange={(e) => setAccess({ ...access, available_datetime: e.target.value })} placeholder="Available date/time (e.g. Mon 9am–5pm)" />
              <div className="flex gap-2">
                <Button data-testid="access-submit" disabled={!!busy} onClick={submitAccessInfo} className="bg-amber-600 text-white hover:bg-amber-700">Send to server</Button>
                <Button variant="ghost" onClick={() => setShowAccess(false)} className="text-slate-500">Cancel</Button>
              </div>
            </div>
          ))}

          {!hasSkip && !skipForm && (
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="decision-skip-basic" disabled={!!busy} onClick={() => setSkipForm("basic")}
                className="flex-col items-start gap-0.5 h-auto py-2 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50">
                <span className="text-xs">Skip trace (basic)</span><span className="font-bold">$49</span>
              </Button>
              <Button data-testid="decision-skip-advanced" disabled={!!busy} onClick={() => setSkipForm("advanced")}
                className="flex-col items-start gap-0.5 h-auto py-2 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50">
                <span className="text-xs">Skip trace (advanced)</span><span className="font-bold">$75</span>
              </Button>
            </div>
          )}

          {!hasSkip && skipForm && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="decision-skip-form">
              <p className="text-sm font-semibold text-slate-800">{skipForm === "advanced" ? "Advanced skip trace — $75" : "Basic skip trace — $49"}</p>
              <div>
                <Label htmlFor="st-addr">Last known address</Label>
                <Input id="st-addr" data-testid="decision-skip-address" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Last known address for the defendant" />
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" data-testid="decision-skip-pp" checked={pp} onChange={(e) => setPp(e.target.checked)} className="mt-0.5 h-4 w-4" />
                <span>{PP_TEXT}</span>
              </label>
              <div className="flex gap-2">
                <Button data-testid="decision-skip-submit" disabled={!!busy} onClick={submitSkip} className="bg-amber-600 text-white hover:bg-amber-700">Continue to payment</Button>
                <Button variant="ghost" onClick={() => setSkipForm(null)} className="text-slate-500">Cancel</Button>
              </div>
            </div>
          )}

          {!showAddrForm ? (
            <Button data-testid="decision-extra-address" disabled={!!busy} onClick={() => setShowAddrForm(true)}
              className="w-full justify-between bg-white text-slate-900 border border-slate-300 hover:bg-slate-50">
              <span>Extra address (we attempt a new address)</span><span className="font-bold">$50</span>
            </Button>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="decision-address-form">
              <p className="text-sm font-semibold text-slate-800">New service address — $50</p>
              <Input data-testid="decision-address-street" value={newAddr.street} onChange={(e) => setNewAddr({ ...newAddr, street: e.target.value })} placeholder="Street address" />
              <div className="grid grid-cols-3 gap-2">
                <Input data-testid="decision-address-city" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} placeholder="City" />
                <Input data-testid="decision-address-state" value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} placeholder="State" />
                <Input data-testid="decision-address-zip" value={newAddr.zip} onChange={(e) => setNewAddr({ ...newAddr, zip: e.target.value })} placeholder="ZIP" />
              </div>
              <div className="flex gap-2">
                <Button data-testid="decision-address-submit" disabled={!!busy} onClick={submitAddress} className="bg-amber-600 text-white hover:bg-amber-700">Continue to payment</Button>
                <Button variant="ghost" onClick={() => setShowAddrForm(false)} className="text-slate-500">Cancel</Button>
              </div>
            </div>
          )}

          {!isBarrier && (
            <Button data-testid="decision-additional-attempt" disabled={!!busy} onClick={() => startCheckout("additional_attempt")}
              className="w-full justify-between bg-white text-slate-900 border border-slate-300 hover:bg-slate-50">
              <span>Additional attempt</span><span className="font-bold">$25</span>
            </Button>
          )}

          {isBarrier ? (
            !showClose ? (
              <Button data-testid="decision-close-return" disabled={!!busy} onClick={openClose}
                className="w-full justify-start bg-white text-slate-700 border border-slate-300 hover:bg-slate-50">
                Close with a Return of Non-Service
              </Button>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="decision-close-form">
                <p className="text-sm font-semibold text-slate-800">Close the order</p>
                <div className="space-y-1">
                  {CLOSE_REASONS.map(([val, label]) => (
                    <label key={val} className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                      <input type="radio" name="close-reason" data-testid={`close-reason-${val}`} checked={closeReason === val} onChange={() => setCloseReason(val)} className="mt-0.5 h-4 w-4" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {closeReason === "other" && (
                  <Input data-testid="close-reason-text" value={closeText} onChange={(e) => setCloseText(e.target.value)} placeholder="Describe the reason *" />
                )}
                {preview && (
                  <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-700" data-testid="close-preview">
                    <div className="flex justify-between"><span>Documented service attempt(s)</span><b>{usd(preview.documented_service_attempts)}</b></div>
                    <div className="flex justify-between"><span>Administrative fee</span><b>{usd(preview.administrative_fee)}</b></div>
                    <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-emerald-700"><span>Refund</span><b>{usd(preview.refund)}</b></div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button data-testid="close-submit" disabled={!!busy} onClick={submitBarrierClose} className="bg-red-600 text-white hover:bg-red-700">Confirm close</Button>
                  <Button variant="ghost" onClick={() => setShowClose(false)} className="text-slate-500">Cancel</Button>
                </div>
              </div>
            )
          ) : (
            <Button data-testid="decision-close-return" disabled={!!busy} onClick={closeReturn}
              className="w-full justify-start bg-white text-slate-700 border border-slate-300 hover:bg-slate-50">
              Close with a Return of Non-Service (no charge)
            </Button>
          )}

          <p className="text-xs text-slate-500">
            No charge is made automatically. If no choice is made within 5 business days, the order is routed to the server to file a Return of Non-Service.
          </p>
        </div>
      ) : (
        <p className="mt-4 text-sm text-slate-600" data-testid="client-decision-status">
          {cd.selected_choice ? `Awaiting payment for: ${cd.selected_choice.replace(/_/g, " ")}.` : "Awaiting the client's decision."}
        </p>
      )}
    </div>
  );
};
